import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import postMessageToParent from '../../helpers/postMessageToParent';
import useParentWindowCoordination from '../../hooks/useParentWindowCoordination';

import OverlayStatusContext from './OverlayStatusContext';
import type OverlayStatusData from './types/OverlayStatusData';
import useBeforeClose from './useBeforeClose';

type OverlayStatusProviderProps = {
    children: ReactNode;
};

const OverlayStatusProvider = ({ children }: OverlayStatusProviderProps) => {
    const [isClosing, setIsClosing] = useState<boolean>(false);
    const [forcedOverlay, setForcedOverlay] = useState<boolean>(false);
    const [parentSupportsRedirects, setParentSupportsRedirects] =
        useState<boolean>(false);

    const { isProcessingClose, registerBeforeClose, executeBeforeClose } =
        useBeforeClose();

    const closeIframeOverlay = useCallback(() => {
        executeBeforeClose(() => setIsClosing(true));
    }, [executeBeforeClose]);

    const forceOverlay = useCallback(() => {
        setForcedOverlay(true);
    }, []);

    const enableParentRedirects = useCallback(() => {
        setParentSupportsRedirects(true);
    }, []);

    const openerUrl = useParentWindowCoordination();

    useEffect(() => {
        if (!isClosing) {
            return;
        }

        postMessageToParent({
            type: 'lb-close',
        });
    }, [isClosing]);

    const value = useMemo<OverlayStatusData>(
        () => ({
            overlayIsClosing: isClosing,
            closeOverlay: closeIframeOverlay,
            forceOverlay: forceOverlay,
            enableParentRedirects: enableParentRedirects,
            registerBeforeClose: registerBeforeClose,
            isProcessingClose: isProcessingClose,
            inOverlay: window.top !== window || forcedOverlay || false,
            openerUrl: openerUrl,
            parentSupportsRedirects: parentSupportsRedirects,
        }),
        [
            isClosing,
            closeIframeOverlay,
            forcedOverlay,
            forceOverlay,
            enableParentRedirects,
            registerBeforeClose,
            isProcessingClose,
            openerUrl,
            parentSupportsRedirects,
        ]
    );

    return (
        <OverlayStatusContext.Provider value={value}>
            {children}
        </OverlayStatusContext.Provider>
    );
};

export default OverlayStatusProvider;
